import type { IconName } from '@civilgrid/ui/components';
import { Button, IconButton, SkeletonLoader, Tooltip } from '@civilgrid/ui/components';
import { useNavigate, useParams } from '@tanstack/react-router';
import type React from 'react';
import { useMemo, useRef } from 'react';
import { useSnapshot } from 'valtio';
import { Action } from '~/authorizations';
import { useActionPermission } from '~/authorizations/useActionPermission';
import { capturePosthogEvent, captureTypedPosthogEvent, POSTHOG_EVENTS } from '~/common/posthogEvents';
import { useQuery } from '~/common/routerUtils';
import CloseIconButton from '~/components2/forms/CloseIconButton';
import rootStore from '~/store';
import type { ProjectDetailsParams } from '../..';
import { EXPORT_TAB_ID } from '../../exportTabIds';
import projectDetailsStore from '../../store';
import store from '../store';
import { useUtilitiesPageRedesignEnabled } from '../useUtilitiesPageRedesignEnabled';
import AgencyHistoryDialog from './AgencyHistoryDialog';
import { shouldShowAgencyHistory } from './agencyHistoryView';
import ContactsDialog from './ContactsDialog';
import ExportsMenu from './ExportsMenu';
import { getTopNavGates, type TopNavGates } from './gating';

export default function TopNav() {
  const { isExpanded } = useSnapshot(projectDetailsStore) as typeof projectDetailsStore;
  const { projectId, tabId } = useParams({ strict: false }) as ProjectDetailsParams;
  const { notifications, project, isError, isLoading } = useSnapshot(store) as typeof store;
  const { currentOrganization, me } = useSnapshot(rootStore) as typeof rootStore;
  const navigate = useNavigate();
  const isPageRedesignEnabled = useUtilitiesPageRedesignEnabled();
  const canDownloadData = useActionPermission(Action.DownloadData);

  const { ready, showDownloads, showDrawerButtons, showContacts, showExports, showReports, downloadsTurnedOff } =
    useSettledGates(
      projectId,
      getTopNavGates({
        projectId,
        project,
        subscription: currentOrganization?.subscription,
        orgResolved: !!currentOrganization,
        isError,
        isPageRedesignEnabled,
      }),
    );

  const tabName = useMemo(() => {
    if (isExpanded) {
      return 'Utilities - Details';
    } else {
      return 'Utilities - Summary';
    }
  }, [isExpanded]);

  const handleToggleExpanded = () => {
    projectDetailsStore.toggleIsExpanded();
    capturePosthogEvent(POSTHOG_EVENTS.civilsearchTableExpandToggled, {
      project_id: projectId,
      expanded: !isExpanded,
    });
  };

  return (
    <div className="flex items-center justify-between border-b p-3" data-testid="utilities-top-nav">
      <div id="utilities-top-nav-title" className="text-base font-medium" data-testid="utilities-top-nav-title">
        {tabName}
      </div>
      <div className="flex items-center space-x-2">
        {/* CG-793: internal diagnostic — admin-gated only, no feature flag. Sits outside the
            `ready` gate so it stays available while the project's tier gates settle. */}
        {shouldShowAgencyHistory({ isAdmin: me?.isAdmin, projectId }) && <AgencyHistoryDialog projectId={projectId} />}
        {ready ? (
          <>
            {isExpanded ? (
              <Tooltip asChild text="Collapse Table">
                <IconButton
                  icon="arrowRight"
                  variant="secondary"
                  size="icon"
                  iconSize={16}
                  onClick={handleToggleExpanded}
                  aria-label="Collapse Table"
                  className="p-1.5"
                  data-testid="utilities-top-nav-collapse-table"
                />
              </Tooltip>
            ) : (
              <Button variant="secondary" onClick={handleToggleExpanded} data-testid="utilities-top-nav-view-details">
                View Details
              </Button>
            )}
            {/* Gated on Downloads alone (not the full CivilSearch experience) so it also shows on the
                Lite tier when Downloads is enabled for that org/tier. */}
            {showExports && (
              <ExportsMenu
                hasDownloadsFeature={showDownloads}
                downloadsTurnedOff={downloadsTurnedOff}
                hasReportsFeature={showReports}
                isFullUtilitiesExperience={showDrawerButtons}
              />
            )}
            {!isPageRedesignEnabled && showDownloads && canDownloadData && (
              <Button
                variant="secondary"
                onClick={() => {
                  navigate({ to: '/projects/$projectId/$tabId', params: { projectId, tabId: EXPORT_TAB_ID } });
                }}
                data-testid="utilities-top-nav-download-data"
              >
                Download Data
              </Button>
            )}

            {/* The store keeps the previous project's agencies until the current project's finish loading. */}
            {showContacts && <ContactsDialog disabled={isLoading || isError} />}

            <Button
              onClick={() => {
                captureTypedPosthogEvent(POSTHOG_EVENTS.civilsearchRequestResearchClicked, { project_id: projectId });
                navigate({
                  to: '/projects/$projectId/$tabId',
                  params: { projectId, tabId },
                  search: { drawer: 'request-data' },
                });
              }}
              data-testid="utilities-top-nav-request-data"
            >
              {isPageRedesignEnabled ? 'Request Research' : 'Request Data'}
            </Button>

            {showDrawerButtons && (
              <>
                <DrawerIconButton
                  id="activity"
                  icon="activityLog"
                  notification={notifications.activity}
                  tooltip="Activity Feed"
                />
                <DrawerIconButton
                  id="project-channel"
                  icon="chatBubble"
                  notification={notifications.message}
                  tooltip="Project Channel"
                />
              </>
            )}
          </>
        ) : (
          <ActionsSkeleton />
        )}

        <CloseIconButton
          aria-label="Close utilities"
          data-testid="utilities-top-nav-close"
          onClick={() => {
            navigate({ to: '/projects/$projectId', params: { projectId } });
          }}
        />
      </div>
    </div>
  );
}

/**
 * Holds the first fully-resolved gates for the current project. The utilities store clears `project`
 * on every refetch, which would otherwise pull the buttons back out of the bar and shift the rest.
 */
function useSettledGates(projectId: string, gates: TopNavGates): TopNavGates {
  const settled = useRef<{ projectId: string; gates: TopNavGates } | null>(null);

  if (gates.ready) {
    settled.current = { projectId, gates };
  } else if (settled.current?.projectId !== projectId) {
    settled.current = null;
  }

  return settled.current?.gates ?? gates;
}

/**
 * Loading affordance for the action buttons. Deliberately a single pill rather than a reserved
 * footprint: the button set depends on the project tier, so no fixed width is right for every tier.
 */
function ActionsSkeleton() {
  return <SkeletonLoader width={93} height={30} aria-hidden data-testid="utilities-top-nav-loading" />;
}

type DrawerIconButtonProps = {
  id: string;
  icon: IconName;
  notification?: boolean;
  tooltip: string;
};

function DrawerIconButton({ id, icon, notification, tooltip }: DrawerIconButtonProps) {
  const navigate = useNavigate();
  const { projectId, tabId } = useParams({ strict: false }) as ProjectDetailsParams;
  const query = useQuery();

  const pushToDrawer = (drawer: string) => {
    navigate({ to: '/projects/$projectId/$tabId', params: { projectId, tabId }, search: { drawer } });
  };

  const drawerId = query.get('drawer');
  const isActive = drawerId === id;

  return (
    <div className="relative inline-flex">
      <Tooltip asChild text={tooltip}>
        <IconButton
          icon={icon}
          variant={isActive ? 'selected' : 'hollow'}
          size="icon"
          iconSize={16}
          onClick={() => pushToDrawer(id)}
          aria-label={tooltip}
          className="p-1.5"
          data-testid={`utilities-top-nav-drawer-${id}`}
        />
      </Tooltip>
      {notification && <div className="absolute right-0 top-0 h-2 w-2 rounded-full bg-brand" aria-hidden />}
    </div>
  );
}
